import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';

/**
 * POST /api/wallet/payment-methods/setup
 * Setup a new payment method
 */

/**
 * Request schema for setting up a payment method
 */
export const SetupPaymentMethodRequestSchema = z.object({
  /** Price for the setup intent (optional, used for off-session payments) */
  price: z.number().positive().optional(),
});

export type SetupPaymentMethodRequest = z.infer<typeof SetupPaymentMethodRequestSchema>;

/**
 * Success response schema for payment method setup
 */
export const SetupPaymentMethodSuccessSchema = z.object({
  success: z.literal(true),
  /** Client secret for the SetupIntent to confirm in the frontend */
  clientSecret: z.string().min(1),
});

export type SetupPaymentMethodSuccess = z.infer<typeof SetupPaymentMethodSuccessSchema>;

/**
 * Complete response schema for payment method setup
 * Discriminated union of success and error responses
 */
export const SetupPaymentMethodResponseSchema = z.discriminatedUnion('success', [
  SetupPaymentMethodSuccessSchema,
  ErrorResponseSchema,
]);

export type SetupPaymentMethodResponse = z.infer<typeof SetupPaymentMethodResponseSchema>;

/**
 * GET /api/wallet/payment-methods/all
 * Get all payment methods for a user
 */

/**
 * Payment method card details schema
 */
export const PaymentMethodCardSchema = z.object({
  /** Card brand (visa, mastercard, etc.) */
  brand: z.string(),
  /** Last 4 digits of the card */
  last4: z.string().length(4),
  /** Expiration month */
  exp_month: z.number().int().min(1).max(12),
  /** Expiration year */
  exp_year: z.number().int(),
  /** Card funding type */
  funding: z.string().optional(),
});

export type PaymentMethodCard = z.infer<typeof PaymentMethodCardSchema>;

/**
 * Payment method schema (Stripe PaymentMethod object)
 */
export const PaymentMethodSchema = z.object({
  /** Stripe payment method ID */
  id: z.string(),
  /** Object type (always "payment_method") */
  object: z.literal('payment_method'),
  /** Billing details */
  billing_details: z.object({
    address: z.object({
      city: z.string().nullable(),
      country: z.string().nullable(),
      line1: z.string().nullable(),
      line2: z.string().nullable(),
      postal_code: z.string().nullable(),
      state: z.string().nullable(),
    }).optional(),
    email: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
  }).optional(),
  /** Card details */
  card: PaymentMethodCardSchema.optional(),
  /** Bank-debit details (type us_bank_account) -- dropping it left the card face blank */
  us_bank_account: z.object({
    bank_name: z.string().nullable().optional(),
    last4: z.string().nullable().optional(),
    account_type: z.string().nullable().optional(),
  }).optional(),
  /** Creation timestamp */
  created: z.number().int(),
  /** Associated customer ID */
  customer: z.string().nullable(),
  /** Whether in live mode */
  livemode: z.boolean(),
  /** Metadata */
  metadata: z.record(z.string(), z.string()).optional(),
  /** Payment method type */
  type: z.string(),
});

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * Success response schema for getting all payment methods
 */
export const GetAllPaymentMethodsSuccessSchema = z.object({
  success: z.literal(true),
  /** Array of payment methods */
  paymentMethods: z.array(PaymentMethodSchema),
});

export type GetAllPaymentMethodsSuccess = z.infer<typeof GetAllPaymentMethodsSuccessSchema>;

/**
 * Complete response schema for getting all payment methods
 * Discriminated union of success and error responses
 */
export const GetAllPaymentMethodsResponseSchema = z.discriminatedUnion('success', [
  GetAllPaymentMethodsSuccessSchema,
  ErrorResponseSchema,
]);

export type GetAllPaymentMethodsResponse = z.infer<typeof GetAllPaymentMethodsResponseSchema>;

/**
 * DELETE /api/wallet/payment-methods/remove
 * Remove a payment method
 */

/**
 * Request schema for removing a payment method
 */
export const RemovePaymentMethodRequestSchema = z.object({
  /** Stripe payment method ID to remove */
  paymentMethodId: z.string({ error: 'paymentMethodId is required' }).min(1, 'paymentMethodId is required'),
});

export type RemovePaymentMethodRequest = z.infer<typeof RemovePaymentMethodRequestSchema>;

/**
 * Success response schema for removing a payment method
 */
export const RemovePaymentMethodSuccessSchema = z.object({
  success: z.literal(true),
  /** Whether the payment method was successfully removed */
  removed: z.boolean().optional(),
});

export type RemovePaymentMethodSuccess = z.infer<typeof RemovePaymentMethodSuccessSchema>;

/**
 * Complete response schema for removing a payment method
 * Discriminated union of success and error responses
 */
export const RemovePaymentMethodResponseSchema = z.discriminatedUnion('success', [
  RemovePaymentMethodSuccessSchema,
  ErrorResponseSchema,
]);

export type RemovePaymentMethodResponse = z.infer<typeof RemovePaymentMethodResponseSchema>;
